import React, { useMemo, useState } from 'react'
import { OrderDetail } from '../../../../../../modules/API/Orders/models/detailsProductCustomer'
import styled from 'styled-components'
import { IPackagesItem } from '../../../../../../modules/API/Defaults/models/packages'
import StatusCircle from '../../../../../../generic/ui/badge/StatusCircle'
import PackageSelector from '../../../../../../generic/ui/Selectors/PackageSelector'
import IconTextField from '../../../../../../generic/ui/Input/IconTextField'
import HashIcon from '../../../../../../generic/icons/IconLib/HashIcon'
import PencilIcon from '../../../../../../generic/icons/IconLib/PencilIcon'
import UserIcon from '../../../../../../generic/icons/IconLib/UserIcon'

interface Props extends OrderDetail {
  packages: IPackagesItem[]
  handleChange: (item: OrderDetail) => void
  index: number
  item: OrderDetail
}

const ProductModalNestedItem = ({
  packages,
  PackID,
  OrderPacks,
  Status,
  item,
  handleChange
}: Props) => {
  const [packageID, setPackageID] = useState(PackID)
  const [orderPacks, setOrderPacks] = useState(OrderPacks)
  const [isOpenRemarkField, setIsOpenRemarkField] = useState<boolean>(false)
  const [remark, setRemark] = useState<string>(item.Remarks.trim())

  const packageObj = useMemo(
    () => packages.find(p => p.PackID === packageID) ?? null,
    [packages, packageID]
  )

  const handleRemarkField = () => {
    setIsOpenRemarkField(!isOpenRemarkField)
  }
  const handleChangeQty = (qty: number) => {
    setOrderPacks(qty)
    handleChange({ ...item, PackID: packageID, OrderPacks: qty })
  }
  const handleChangePackage = (id: number) => {
    setPackageID(id)
    handleChange({ ...item, PackID: id, OrderPacks: orderPacks })
  }
  const handleChangeRemark = (value: string) => {
    setRemark(value)
    handleChange({ ...item, Remarks: value })
  }

  return (
    <Row>
      <Header>
        <NameSlot>
          <IconSlot aria-hidden><UserIcon /></IconSlot>
          <Name>{item.Customer_Name.trim()}</Name>
        </NameSlot>
        <StatusCircle statusCode={Status} />
      </Header>
      <Controls>
        <RemarkToggle
          type='button'
          $active={remark.trim() !== ''}
          onClick={handleRemarkField}
          aria-label='הערה'
        >
          <PencilIcon />
        </RemarkToggle>
        <PackageSlot>
          <PackageSelector
            packages={packages}
            value={packageObj}
            onSelect={(p) => handleChangePackage(p?.PackID ?? 0)}
          />
        </PackageSlot>
        <QtySlot>
          <IconTextField
            type='number'
            value={orderPacks}
            onChange={(e) => handleChangeQty(Number(e.target.value))}
            leadingIcon={<HashIcon />}
          />
        </QtySlot>
      </Controls>
      {isOpenRemarkField && (
        <RemarkArea
          placeholder='הערה'
          value={remark}
          onChange={(e) => handleChangeRemark(e.target.value)}
          rows={2}
        />
      )}
    </Row>
  )
}

export default ProductModalNestedItem

const Row = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: ${({ theme }) => theme.radii.md};
  direction: rtl;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`

const NameSlot = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  min-width: 0;
`

const IconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.ink3};

  svg {
    width: 1rem;
    height: 1rem;
  }
`

const Name = styled.span`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.md};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.ink};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const QtySlot = styled.div`
  width: 6rem;
  flex-shrink: 0;
`

const PackageSlot = styled.div`
  flex: 1;
  min-width: 0;
`

const RemarkToggle = styled.button<{ $active?: boolean }>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.875rem;
  height: 2.875rem;
  padding: 0;
  border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.accent : theme.colors.inputBorder)};
  background: ${({ theme, $active }) => ($active ? theme.colors.accentSoft : theme.colors.surface)};
  color: ${({ theme, $active }) => ($active ? theme.colors.ink : theme.colors.icon)};
  border-radius: ${({ theme }) => theme.radii.md};
  cursor: pointer;
  transition: all ${({ theme }) => theme.motion.fast} ease;

  svg {
    width: 1.125rem;
    height: 1.125rem;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
  }
`

const RemarkArea = styled.textarea`
  width: 100%;
  min-height: 3.5rem;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.inputBorder};
  border-radius: ${({ theme }) => theme.radii.md};
  padding: 0.5rem 0.75rem;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.md};
  color: ${({ theme }) => theme.colors.ink};
  outline: none;
  resize: vertical;
  direction: rtl;

  &::placeholder {
    color: ${({ theme }) => theme.colors.ink4};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.ink};
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }
`
